import { passkeyClient } from '@better-auth/passkey/client';
import { createAuthClient } from 'better-auth/client';

export interface PasskeyCredential {
  id: string;
  name?: string;
  userId: string;
  credentialID: string;
  deviceType: 'singleDevice' | 'multiDevice';
  backedUp: boolean;
  transports?: string;
  createdAt: string | Date;
  aaguid?: string;
}

interface PasskeyApiError {
  error?: {
    message?: string;
  };
  message?: string;
}

const clients = new Map<string, ReturnType<typeof createPasskeyClient>>();

function createPasskeyClient(baseURL: string) {
  return createAuthClient({
    baseURL,
    plugins: [passkeyClient()],
  });
}

function getPasskeyClient(baseURL: string) {
  const normalizedBaseURL = baseURL.replace(/\/+$/, '');
  const existing = clients.get(normalizedBaseURL);
  if (existing) {
    return existing;
  }

  const client = createPasskeyClient(normalizedBaseURL);
  clients.set(normalizedBaseURL, client);
  return client;
}

function errorMessage(error: { message?: string } | null | undefined, fallback: string): string {
  return error?.message || fallback;
}

async function passkeyRequest<T>(
  baseURL: string,
  path: string,
  init?: RequestInit
): Promise<T> {
  const response = await fetch(`${baseURL.replace(/\/+$/, '')}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  const payload = (await response.json().catch(() => ({}))) as T & PasskeyApiError;

  if (!response.ok) {
    throw new Error(
      payload.error?.message || payload.message || `Passkey request failed (${response.status}).`
    );
  }

  return payload;
}

export function supportsPasskeys(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof window.PublicKeyCredential !== 'undefined'
  );
}

export async function signInWithPasskey(baseURL: string): Promise<void> {
  const result = await getPasskeyClient(baseURL).signIn.passkey();
  if (result.error) {
    throw new Error(errorMessage(result.error, 'Passkey sign-in failed.'));
  }
}

export async function addPasskey(baseURL: string, name?: string): Promise<PasskeyCredential> {
  const result = await getPasskeyClient(baseURL).passkey.addPasskey({
    name: name?.trim() || undefined,
  });
  if (result.error || !result.data) {
    throw new Error(errorMessage(result.error, 'Unable to add passkey.'));
  }
  return result.data as PasskeyCredential;
}

export function listPasskeys(baseURL: string): Promise<PasskeyCredential[]> {
  return passkeyRequest<PasskeyCredential[]>(baseURL, '/passkey/list-user-passkeys');
}

export async function renamePasskey(
  baseURL: string,
  id: string,
  name: string
): Promise<PasskeyCredential> {
  const payload = await passkeyRequest<{ passkey: PasskeyCredential }>(
    baseURL,
    '/passkey/update-passkey',
    {
      method: 'POST',
      body: JSON.stringify({ id, name: name.trim() }),
    }
  );
  return payload.passkey;
}

export async function removePasskey(baseURL: string, id: string): Promise<void> {
  await passkeyRequest<{ status: boolean }>(baseURL, '/passkey/delete-passkey', {
    method: 'POST',
    body: JSON.stringify({ id }),
  });
}
